import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, Users, Laptop, Microscope, Target } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const AboutPage = () => {
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        const data = await api.portfolio.getAll();
        setPortfolioData(data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
        toast({
          title: 'Error',
          description: 'Failed to load portfolio data.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!portfolioData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600">Failed to load data.</p>
      </div>
    );
  }

  return (
    <section className="py-20 bg-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">About Me</h1>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className="border-blue-100 hover:shadow-lg transition-shadow duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Target className="w-6 h-6 text-blue-600" />
                Professional Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                {portfolioData.about.summary}
              </p>
            </CardContent>
          </Card>

          <Card className="border-blue-100 hover:shadow-lg transition-shadow duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <BookOpen className="w-6 h-6 text-blue-600" />
                Leadership Philosophy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                {portfolioData.about.philosophy}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Core Competencies */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Core Competencies</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-6">
          {portfolioData.about.competencies.map((competency, index) => {
            const IconComponent = { BookOpen, Microscope, Users, Laptop }[competency.icon];
            return (
              <div key={index} className="text-center p-6 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-300">
                <div className="flex justify-center mb-3">
                  {IconComponent && <IconComponent className="w-12 h-12 text-blue-600" />}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{competency.title}</h3>
                <p className="text-sm text-gray-600">{competency.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutPage;
